export interface DeviceInfo {
  type: 'mobile' | 'tablet' | 'desktop';
  browser: string;
  browserVersion?: string;
  os: string;
  deviceName: string;
  userAgent: string;
  screenSize: string;
  isIOS?: boolean;
  
}

export function getDeviceInfo(): DeviceInfo {
  const ua = navigator.userAgent;
  const screenSize = `${window.innerWidth}x${window.innerHeight}`;
  
  // Determine device type
  const isMobile = /iPhone|iPod|Android|BlackBerry|IEMobile|Opera Mini/i.test(ua) && window.innerWidth < 768;
  const isTablet = (/iPad|Android/i.test(ua) && !/Mobile/i.test(ua)) || 
                    (window.innerWidth >= 768 && window.innerWidth < 1024);
  
  // Determine browser
  const isChrome = /Chrome/i.test(ua) && !/Edge|Edg/i.test(ua);
  const isFirefox = /Firefox/i.test(ua);
  const isSafari = /Safari/i.test(ua) && !/Chrome/i.test(ua);
  const isEdge = /Edge|Edg/i.test(ua);
  const isIE = /Trident/i.test(ua);
  const isOpera = /OPR|Opera/i.test(ua);
  
  let browser = 'Unknown';
  let browserVersion: string | undefined;
  
  if (isChrome) {
    browser = 'Chrome';
    const match = ua.match(/Chrome\/(\d+\.\d+)/);
    browserVersion = match ? match[1] : undefined;
  } else if (isFirefox) {
    browser = 'Firefox';
    const match = ua.match(/Firefox\/(\d+\.\d+)/);
    browserVersion = match ? match[1] : undefined;
  } else if (isSafari) {
    browser = 'Safari';
    // Safari UA contains "Version/X.Y" for the Safari version
    const match = ua.match(/Version\/(\d+\.\d+)/);
    browserVersion = match ? match[1] : undefined;
  } else if (isEdge) {
    browser = 'Edge';
    const match = ua.match(/Edge\/(\d+\.\d+)/) || ua.match(/Edg\/(\d+\.\d+)/);
    browserVersion = match ? match[1] : undefined;
  } else if (isIE) {
    browser = 'Internet Explorer';
    const match = ua.match(/rv:(\d+\.\d+)/) || ua.match(/MSIE (\d+\.\d+)/);
    browserVersion = match ? match[1] : undefined;
  } else if (isOpera) {
    browser = 'Opera';
    const match = ua.match(/OPR\/(\d+\.\d+)/) || ua.match(/Opera\/(\d+\.\d+)/);
    browserVersion = match ? match[1] : undefined;
  }
  
  // Determine OS
  const isWindows = /Windows/i.test(ua);
  const isMac = /Macintosh|Mac OS X/i.test(ua);
  const isLinux = /Linux/i.test(ua);
  const isIOS = /iPad|iPhone|iPod/i.test(ua);
  const isAndroid = /Android/i.test(ua);
  
  let os = 'Unknown';
  if (isWindows) os = 'Windows';
  else if (isMac) os = 'macOS';
  else if (isIOS) os = 'iOS';
  else if (isAndroid) os = 'Android';
  else if (isLinux) os = 'Linux';
  
  // Determine device name (limited information available)
  let deviceName = 'Unknown';
  if (isIOS) {
    if (/iPhone/i.test(ua)) deviceName = 'iPhone';
    else if (/iPad/i.test(ua)) deviceName = 'iPad';
    else if (/iPod/i.test(ua)) deviceName = 'iPod';
  } else if (isAndroid) {
    const androidDeviceMatch = ua.match(/Android [\d\.]+; ([^;]+)/);
    deviceName = androidDeviceMatch ? androidDeviceMatch[1] : 'Android Device';
  } else if (isWindows) {
    deviceName = 'Windows Device';
  } else if (isMac) {
    deviceName = 'Mac Device';
  }
  
  return {
    type: isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop',
    browser,
    browserVersion,
    os,
    deviceName,
    userAgent: ua,
    screenSize
  };
}

/**
 * Checks if the current browser is Safari with a version below the specified minimum version
 * @param minVersion Minimum required Safari version (default: 15.0)
 * @returns true if browser is Safari with version below minVersion, false otherwise
 */
export function isOutdatedSafari(minVersion: number = 14.0): boolean {
  const deviceInfo = getDeviceInfo();
  
  if (deviceInfo.browser !== 'Safari' || !deviceInfo.browserVersion) {
    return false;
  }
  
  // Parse Safari version and compare with minimum version
  const currentVersion = parseFloat(deviceInfo.browserVersion);
  return !isNaN(currentVersion) && currentVersion < minVersion;
}
